import { Box, Button, Grid, Paper, useTheme, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useCreateAnnual } from "../../../../hooks/useCreateReport";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

type ConfirmReportDialogProps = {
  open: boolean;
  onClose: () => void;
  reportingYear: number;
  facilityId: string;
};

function ConfirmReportDialog({
  open,
  onClose,
  reportingYear,
  facilityId,
}: ConfirmReportDialogProps) {
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();

  const facility = data?.tierIIReport?.facility;

  const [createReport, { loading }] = useCreateAnnual();
  const navigate = useNavigate();

  const handleCreateReport = useCallback(async () => {
    if (!facilityId || !reportingYear) {
      alerts.info(
        "Facility ID or reporting year missing. Report cannot be created"
      );
      return;
    }

    try {
      const result = await createReport({
        variables: {
          facilityId,
          reportingYear,
        },
      });

      // Navigate to the created report
      if (result.data?.createTierIIReport?.id) {
        navigate(`/staff/fulfillment/r/${result.data.createTierIIReport.id}`, {
          replace: true,
        });
      }
    } catch (err) {
      console.error("Failed to create report:", err);
      alerts.error("Failed to create TierIIReport:", err);
    }
  }, [createReport, facilityId, reportingYear, navigate]);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      header={`Create Annual RY${reportingYear} Report`}
      confirmText="Yes, I'm sure"
      cancelText="No, cancel"
      onConfirm={handleCreateReport}
      info={
        <Typography>
          Are you sure you want to create an annual RY{reportingYear} report for
          this facility?
          <br />
          <br />
          <b>Name:</b> {facility?.name}
          <br />
          <b>Address:</b> {facility?.streetAddress1} {facility?.streetAddress2}
          {facility?.city}, {facility?.state} {facility?.zip}
        </Typography>
      }
      loading={loading}
      disableConfirm={!facilityId || !reportingYear}
    />
  );
}

export function CreateReport() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { facilityId, reportingYear: reportingYearParam } = useParams<{
    facilityId: string;
    reportingYear: string;
  }>();

  const reportingYear = useMemo(() => {
    if (!reportingYearParam) return null;

    const cleanedReportingYear = reportingYearParam.replace(/,/g, "");
    // ast-grep-ignore: no-unsafe-number-parsing
    const reportingYear = parseInt(cleanedReportingYear);

    if (isNaN(reportingYear)) return null;

    return reportingYear;
  }, [reportingYearParam]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(4),
        width: "100%",
      }}
    >
      <Typography>🪁 No Active Workflow</Typography>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        disabled={!facilityId || !reportingYearParam}
      >
        Create Annual Report
      </Button>
      {facilityId && reportingYear && open && (
        <ConfirmReportDialog
          open={open}
          onClose={() => setOpen(false)}
          reportingYear={reportingYear}
          facilityId={facilityId}
        />
      )}
    </Box>
  );
}
